import React, { useState, useEffect, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Lock, Unlock, Mic, MicOff, FileText } from "lucide-react";
import { useDesignSystem, cn } from '../../designSystem';

// Advanced Responsive Header Component
const ResponsiveHeader = ({
  title,
  isRecording,
  transcriptionLanguage,
  handleLanguageChange,
  languageOptions,
  generateNote,
  isGeneratingNote,
  toggleRecordingForCurrent,
  windowWidth
}) => {
  const { colors, typography, componentStyles, effects, shadows } = useDesignSystem();
  const [layoutMode, setLayoutMode] = useState('full'); // 'full', 'compact', or 'minimal'
  const headerRef = useRef(null);
  
  // More granular responsive behavior - using a longer window before compressing
  useEffect(() => {
    const checkLayout = () => {
      const headerWidth = headerRef.current?.offsetWidth || 0;
      
      if (headerWidth < 550) {
        setLayoutMode('minimal');
      } else if (headerWidth < 750) {
        setLayoutMode('compact');
      } else {
        setLayoutMode('full');
      }
    };
    
    checkLayout();
    window.addEventListener('resize', checkLayout);
    return () => window.removeEventListener('resize', checkLayout);
  }, [windowWidth]);

  // Matchet større fontstørrelse med NoteTab
  const getTitleClass = () => {
    if (layoutMode === 'minimal') {
      return cn(typography.body, "font-medium"); // Økt fra xsmall til body
    } else if (layoutMode === 'compact') {
      return cn(typography.h1, "font-medium"); // Økt fra body til h4
    } else {
      return cn(typography.h1, "font-medium"); // Matched med NoteTab
    }
  };

  return (
    <div ref={headerRef} className="flex justify-between items-center w-full">
      {/* Left side - Title and recording indicators */}
      <div className="flex items-center space-x-2">
        {/* Always show title but with adaptive size */}
        <span className={cn(getTitleClass(), colors.neutral.textPrimary)}>
          {title || "Transkripsjon"}
        </span>
        
        {/* Recording indicator - always show */}
        {isRecording && (
          <div className="flex space-x-1">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse delay-100"></div>
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse delay-200"></div>
          </div>
        )}
      </div>
      
      {/* Right side - Controls with consistent padding and sizing */}
      <div className="flex items-center space-x-2 ml-auto">
        {/* Language selector - wider for better tap target */}
        <Select value={transcriptionLanguage} onValueChange={handleLanguageChange}>
          <SelectTrigger className={cn(
            layoutMode === 'minimal' ? "w-[48px] px-2" : "w-[70px] px-3",
            "h-10", // Consistent height
            colors.primary.border,
            colors.primary.focusRing,
            "dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300"
          )}>
            <SelectValue>
              <div className="flex items-center gap-1">
                <span>{languageOptions.find(lang => lang.value === transcriptionLanguage)?.flag}</span>
                {layoutMode !== 'minimal' && (
                  <span className="text-xs">
                    {transcriptionLanguage.split('-')[0].toUpperCase()}
                  </span>
                )}
              </div>
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {languageOptions.map(({ value, label, flag }) => (
              <SelectItem key={value} value={value} className="flex items-center gap-2">
                <span>{flag}</span> {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {/* Generate Note button - wider and consistent height */}
        <Button
          onClick={generateNote}
          className={cn(
            componentStyles.button.base,
            componentStyles.button.primary,
            shadows.sm,
            "whitespace-nowrap h-10", // Consistent height
            layoutMode === 'minimal' ? "px-3 min-w-[40px]" : 
            layoutMode === 'compact' ? "px-3 min-w-[80px]" : "px-4 min-w-[140px]",
            isGeneratingNote && componentStyles.button.states.disabled
          )}
          disabled={isGeneratingNote}
        >
          <FileText className={cn(layoutMode === 'full' ? "mr-2" : "mr-0", "h-5 w-5")} />
          {layoutMode === 'minimal' ? null : 
           layoutMode === 'compact' ? "Generer" : "Generer notat"}
        </Button>
        
        {/* Record button - wider and consistent height */}
        <Button
          onClick={toggleRecordingForCurrent}
          variant={isRecording ? "outline" : "default"}
          className={cn(
            isRecording 
              ? cn("border-red-200 text-red-600 hover:bg-red-50 dark:border-red-400 dark:text-red-400") 
              : componentStyles.button.primary,
            effects.transition,
            shadows.sm,
            "h-10", // Consistent height
            layoutMode === 'minimal' ? "px-3 min-w-[40px]" : 
            layoutMode === 'compact' ? "px-3 min-w-[80px]" : "px-4 min-w-[140px]",
          )}
        >
          {isRecording ? (
            <div className="flex items-center">
              <MicOff className="h-5 w-5" />
              {layoutMode === 'minimal' ? null : 
               layoutMode === 'compact' ? <span className="ml-2">Stopp</span> : <span className="ml-2">Stopp opptak</span>}
            </div>
          ) : (
            <div className="flex items-center">
              <Mic className="h-5 w-5" />
              {layoutMode === 'minimal' ? null : 
               layoutMode === 'compact' ? <span className="ml-2">Start</span> : <span className="ml-2">Start opptak</span>}
            </div>
          )}
        </Button>
      </div>
    </div>
  );
};

const TranscriptionTab = ({
  transcription,
  setTranscription,
  currentKonsultasjon,
  updateKonsultasjon,
  isRecording,
  isTranscriptionLocked,
  setIsTranscriptionLocked,
  contextText,
  setContextText,
  isLandscape,
  transcriptionLanguage,
  handleLanguageChange,
  languageOptions,
  generateNote,
  isGeneratingNote,
  toggleRecordingForCurrent,
  isTabletView,
  isDesktopView
}) => {
  const { colors, typography, componentStyles, effects, shadows } = useDesignSystem();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Track window width for responsive adjustments
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Consistent content padding across all views
  const contentPadding = {
    desktop: "p-6",
    tablet: "p-5",
    mobile: "p-4",
    mobileLandscape: "p-3",
  };

  // Desktop-visning
  if (isDesktopView) {
    return (
      <Card className={cn(
        "h-[calc(100vh-220px)]",
        componentStyles.card.glass,
        "overflow-hidden flex flex-col"
      )}>
        <CardHeader className="p-6 pb-3 flex-shrink-0 overflow-visible z-10">
          <CardTitle>
            <ResponsiveHeader
              title="Transkripsjon"
              isRecording={isRecording}
              transcriptionLanguage={transcriptionLanguage}
              handleLanguageChange={handleLanguageChange}
              languageOptions={languageOptions}
              generateNote={generateNote}
              isGeneratingNote={isGeneratingNote}
              toggleRecordingForCurrent={toggleRecordingForCurrent}
              windowWidth={windowWidth}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className={cn(
          "h-[calc(100%-70px)]", // Redusert fra 90px for mindre avstand
          contentPadding.desktop,
          "pt-2", // Redusert fra 0 for å øke avstanden bare litt
          "flex flex-col gap-6"
        )}>
          <div className="flex-1 border-none rounded-md overflow-hidden relative">
            <Button
              onClick={() => setIsTranscriptionLocked(!isTranscriptionLocked)}
              variant="ghost"
              size="icon"
              className={cn(
                "absolute top-2 right-2 h-10 w-10", // Larger button
                "hover:bg-blue-50 dark:hover:bg-neutral-800",
                effects.transition,
                "z-10"
              )}
            >
              {isTranscriptionLocked ? (
                <Lock className={cn(
                  "h-5 w-5", // Larger icon
                  colors.neutral.textPrimary
                )} />
              ) : (
                <Unlock className={cn(
                  "h-5 w-5", // Larger icon
                  colors.primary.text
                )} />
              )}
            </Button>
            
            <ScrollArea className={cn(
              componentStyles.scrollArea.base,
              "h-full"
            )}>
              {transcription || !isTranscriptionLocked ? (
                <div className="p-6">
                  {isTranscriptionLocked ? (
                    <div className={cn(
                      typography.bodyLg,
                      "whitespace-pre-wrap"
                    )}>
                      {transcription}
                    </div>
                  ) : (
                    <Textarea
                      value={transcription}
                      onChange={(e) => {
                        setTranscription(e.target.value);
                        if (currentKonsultasjon) {
                          const updated = { ...currentKonsultasjon, transcript: e.target.value };
                          updateKonsultasjon(updated);
                        }
                      }}
                      className={cn(
                        typography.bodyLg,
                        "w-full min-h-[400px] p-0 border-none focus:ring-0",
                        colors.neutral.bg,
                        "dark:bg-neutral-900",
                        "resize-none"
                      )}
                      placeholder="Skriv inn transkripsjon..."
                    />
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                  <FileText className={cn(
                    "h-12 w-12 mb-4",
                    colors.neutral.textQuinary
                  )} />
                  <p className={cn(
                    "text-center",
                    colors.neutral.textQuaternary,
                    "italic"
                  )}>
                    Transkripsjon vil vises her<br />
                    Start opptak for å begynne
                  </p>
                </div>
              )}
            </ScrollArea>
          </div>
          <div className={cn(
            "rounded-lg p-4 border",
            "bg-blue-50 dark:bg-neutral-800",
            "border-blue-200 dark:border-neutral-700"
          )}>
            <label className={cn(
              "block text-sm font-medium",
              colors.primary.text,
              "mb-2"
            )}>
              Kontekst & Egne notater
            </label>
            <textarea
              value={contextText}
              onChange={(e) => {
                const newContext = e.target.value;
                setContextText(newContext);
                if (currentKonsultasjon) {
                  const updatedConsultation = { ...currentKonsultasjon, kontekst: newContext, transcript: transcription };
                  updateKonsultasjon(updatedConsultation);
                }
              }}
              className={cn(
                "w-full h-24 p-3 rounded-md",
                colors.primary.border,
                "dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300",
                colors.primary.focusRing
              )}
              placeholder="Skriv inn kontekst for samtalen og egne notater..."
            />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Tablet-visning
  if (isTabletView) {
    return (
      <Card className={cn(
        "h-[calc(100vh-220px)]",
        componentStyles.card.glass,
        "overflow-hidden flex flex-col"
      )}>
        <CardHeader className="p-5 pb-3 flex-shrink-0 overflow-visible z-10">
          <CardTitle>
            <ResponsiveHeader
              title="Transkripsjon"
              isRecording={isRecording}
              transcriptionLanguage={transcriptionLanguage}
              handleLanguageChange={handleLanguageChange}
              languageOptions={languageOptions}
              generateNote={generateNote}
              isGeneratingNote={isGeneratingNote}
              toggleRecordingForCurrent={toggleRecordingForCurrent}
              windowWidth={windowWidth}
            />
          </CardTitle>
        </CardHeader>
        <CardContent className={cn(
          "h-[calc(100%-70px)]", // Redusert fra 85px for mindre avstand
          contentPadding.tablet,
          "pt-2", // Redusert fra 0 for å øke avstanden bare litt
          "flex flex-col gap-4"
        )}>
          <div className="flex-1 border-none rounded-md overflow-hidden relative">
            <Button
              onClick={() => setIsTranscriptionLocked(!isTranscriptionLocked)}
              variant="ghost"
              size="icon"
              className={cn(
                "absolute top-2 right-2 h-10 w-10", // Larger button
                "hover:bg-blue-50 dark:hover:bg-neutral-800",
                effects.transition,
                "z-10"
              )}
            >
              {isTranscriptionLocked ? (
                <Lock className={cn(
                  "h-5 w-5", // Larger icon
                  colors.neutral.textPrimary
                )} />
              ) : (
                <Unlock className={cn(
                  "h-5 w-5", // Larger icon
                  colors.primary.text
                )} />
              )}
            </Button>
            
            <ScrollArea className={cn(
              componentStyles.scrollArea.base,
              "h-full"
            )}>
              {transcription || !isTranscriptionLocked ? (
                <div className="p-4">
                  {isTranscriptionLocked ? (
                    <div className={cn(
                      typography.bodyLg,
                      "whitespace-pre-wrap"
                    )}>
                      {transcription}
                    </div>
                  ) : (
                    <Textarea
                      value={transcription}
                      onChange={(e) => {
                        setTranscription(e.target.value);
                        if (currentKonsultasjon) {
                          const updated = { ...currentKonsultasjon, transcript: e.target.value };
                          updateKonsultasjon(updated);
                        }
                      }}
                      className={cn(
                        typography.bodyLg,
                        "w-full min-h-[400px] p-0 border-none focus:ring-0",
                        colors.neutral.bg,
                        "dark:bg-neutral-900",
                        "resize-none"
                      )}
                      placeholder="Skriv inn transkripsjon..."
                    />
                  )}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center min-h-[400px]">
                  <FileText className={cn(
                    "h-12 w-12 mb-4",
                    colors.neutral.textQuinary
                  )} />
                  <p className={cn(
                    "text-center",
                    colors.neutral.textQuaternary,
                    "italic"
                  )}>
                    Transkripsjon vil vises her<br />
                    Start opptak for å begynne
                  </p>
                </div>
              )}
            </ScrollArea>
          </div>
          <div className={cn(
            "rounded-lg p-4 border",
            "bg-blue-50 dark:bg-neutral-800",
            "border-blue-200 dark:border-neutral-700"
          )}>
            <label className={cn(
              "block text-sm font-medium",
              colors.primary.text,
              "mb-2"
            )}>
              Kontekst & Egne notater
            </label>
            <textarea
              value={contextText}
              onChange={(e) => {
                const newContext = e.target.value;
                setContextText(newContext);
                if (currentKonsultasjon) {
                  const updatedConsultation = { ...currentKonsultasjon, kontekst: newContext, transcript: transcription };
                  updateKonsultasjon(updatedConsultation);
                }
              }}
              className={cn(
                "w-full h-24 p-3 rounded-md",
                colors.primary.border,
                "dark:border-neutral-700 dark:bg-neutral-900 dark:text-neutral-300",
                colors.primary.focusRing
              )}
              placeholder="Skriv inn kontekst for samtalen og egne notater..."
            />
          </div>
        </CardContent>
      </Card>
    );
  }
  
  // Mobil-visning (default) - with consistent padding and improved layout
  return (
    <Card className={cn(
      componentStyles.card.glass,
      "border shadow-sm",
      isLandscape ? "h-[calc(100vh-130px)]" : "h-auto"
    )}>
      <CardHeader className={cn(
        isLandscape ? "p-3 pb-2" : "p-4 pb-2",
        "overflow-visible z-10" // Added for better overflow handling
      )}>
        <ResponsiveHeader
          title="Transkripsjon"
          isRecording={isRecording}
          transcriptionLanguage={transcriptionLanguage}
          handleLanguageChange={handleLanguageChange}
          languageOptions={languageOptions}
          generateNote={generateNote}
          isGeneratingNote={isGeneratingNote}
          toggleRecordingForCurrent={toggleRecordingForCurrent}
          windowWidth={windowWidth}
        />
      </CardHeader>
      <CardContent className={cn(
        isLandscape ? contentPadding.mobileLandscape : contentPadding.mobile,
        "pt-1" // Minimal top padding
      )}>
        <div className="relative">
          {isTranscriptionLocked ? (
            <div className={cn(
              isLandscape ? "h-[calc(100vh-180px)]" : "h-[calc(100vh-370px)]",
              "overflow-y-auto p-3",
              isLandscape && "p-2",
              colors.neutral.bg,
              "dark:bg-neutral-800 rounded-md border dark:border-neutral-700"
            )}>
              <div className={cn(
                typography.bodyLg,
                isLandscape && "text-sm",
                "whitespace-pre-wrap"
              )}>
                {transcription || 'Start opptak for å begynne transkribering'}
              </div>
            </div>
          ) : (
            <Textarea
              value={transcription}
              onChange={(e) => {
                setTranscription(e.target.value);
                if (currentKonsultasjon) {
                  const updated = { ...currentKonsultasjon, transcript: e.target.value };
                  updateKonsultasjon(updated);
                }
              }}
              className={cn(
                isLandscape ? "h-[calc(100vh-180px)]" : "h-[calc(100vh-370px)]",
                "resize-none p-3",
                isLandscape && "p-2 text-sm",
                colors.neutral.bg,
                "dark:bg-neutral-800 rounded-md border dark:border-neutral-700",
                colors.neutral.textPrimary
              )}
              placeholder="Start opptak eller skriv inn transkripsjon..."
            />
          )}
          <Button
            onClick={() => setIsTranscriptionLocked(!isTranscriptionLocked)}
            variant="ghost"
            size="icon"
            className={cn(
              "absolute top-2 right-2 h-10 w-10", // Larger button for better touch target
              isLandscape && "h-8 w-8 top-1 right-1",
              effects.glassmorphism
            )}
          >
            {isTranscriptionLocked ? (
              <Lock className={cn(
                isLandscape ? "h-4 w-4" : "h-5 w-5", // Larger icon
                colors.neutral.textSecondary
              )} />
            ) : (
              <Unlock className={cn(
                isLandscape ? "h-4 w-4" : "h-5 w-5", // Larger icon
                colors.primary.text
              )} />
            )}
          </Button>
        </div>

        {!isLandscape && (
          <div className={cn(
            "mt-3 rounded-lg p-3 border", // Consistent padding
            "bg-blue-50/70 dark:bg-neutral-800/70",
            "border-blue-100 dark:border-neutral-700"
          )}>
            <label className={cn(
              "block text-xs font-medium",
              colors.primary.text,
              "mb-1"
            )}>
              Kontekst & notater
            </label>
            <Textarea
              value={contextText}
              onChange={(e) => {
                const newContext = e.target.value;
                setContextText(newContext);
                if (currentKonsultasjon) {
                  const updatedConsultation = {
                    ...currentKonsultasjon,
                    kontekst: newContext,
                    transcript: transcription
                  };
                  updateKonsultasjon(updatedConsultation);
                }
              }}
              className={cn(
                "w-full h-16 resize-none text-sm p-2", // Consistent padding
                colors.neutral.bg,
                colors.primary.border,
                "dark:border-neutral-700",
                colors.neutral.textPrimary
              )}
              placeholder="Legg til kontekst eller egne notater..."
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TranscriptionTab;